import React, { MouseEvent } from "react";
import {
    StyledAvatarWrapper,
    StyledHeaderWrapper,
    StyledHeaderInnerWrapper,
    StyledHeaderInfo,
    StyledHeaderMenu,
    StyledQuestion,
    StyledQuestionRow,
    StyledMultiVotingIcon,
} from "./PollItemHeader.styled";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import IconButton from "@mui/material/IconButton";
import MoreIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useDispatch, useSelector } from "react-redux";
import { IChoice, IPoll, RootStateType } from "@Interfaces";
import { ClosePoll } from "../PollItemClose";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import UserAvatar from "@Components/PollItem/UserAvatar";
import { useTranslation } from "react-i18next";
import { IReport } from "@Interfaces/Entities/Report";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";

interface IPollItem {
    pollId: string;
    channelId: string;
    author: {
        _id: string;
        username: string;
        avatar: {
            public_url?: string;
            fid?: string;
        };
    };
    published: string;
    question: string;
    choices: IChoice[];
    isActive: boolean;
    report: null | IReport;
    totalVotes: number;
    pollForEdit?: IPoll;
    dragHandleProps?: DraggableProvidedDragHandleProps;
    isDraggable?: boolean;
    multiVoting?: boolean;
    channelClosed?: boolean;
}

const PollItemHeader = ({
    pollId,
    channelId,
    author,
    question,
    choices,
    isActive,
    report,
    totalVotes,
    pollForEdit,
    dragHandleProps,
    isDraggable = false,
    multiVoting = false,
    channelClosed = false,
}: IPollItem) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const authenticatedUser = useSelector(
        (state: RootStateType) => state.auth.isAuthenticated
    );
    const userId = useSelector((state: RootStateType) => state.auth.user.id);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { _id: authorId } = author;

    const [openClosePollDialog, setOpenClosePollDialog] = React.useState(false);

    const authoredUser = authorId === userId;
    const hasVotes = totalVotes > 0;
    // Request/View report actions are intentionally omitted in Polls tab

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (
        event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleODeletePoll = (
        event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        event.preventDefault();
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.DeletePollDialog,
                props: {
                    pollId,
                    channelId,
                },
            })
        );
    };

    const closeDialog = () => {
        setOpenClosePollDialog(false);
        setAnchorEl(null);
    };

    const handlePrintDialog = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.ExportToDialog,
                props: {
                    question,
                    pollId,
                    choices,
                    channel_id: channelId,
                    maxWidth: "md",
                },
            })
        );
    };

    const handleEditPoll = (
        event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        event.preventDefault();

        if (!pollForEdit) return;

        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.PollCreatorDialog,
                props: {
                    channelId,
                    poll: pollForEdit,
                    maxWidth: "md",
                },
            })
        );

        setAnchorEl(null);
    };

    return (
        <>
            <StyledHeaderWrapper>
                <StyledHeaderInnerWrapper
                    isDraggable={isDraggable}
                    {...(dragHandleProps || {})}
                >
                    <StyledAvatarWrapper>
                        <UserAvatar
                            username={author.username}
                            avatar={author.avatar}
                            size="medium"
                        />
                    </StyledAvatarWrapper>

                    <StyledHeaderInfo>
                        <StyledQuestionRow>
                            <StyledQuestion>{question}</StyledQuestion>
                            {multiVoting && (
                                <StyledMultiVotingIcon
                                    aria-label={t(
                                        "poll_item.multi_voting_label"
                                    )}
                                    title={t("poll_item.multi_voting_label")}
                                >
                                    <DoneAllIcon fontSize="small" />
                                </StyledMultiVotingIcon>
                            )}
                        </StyledQuestionRow>
                    </StyledHeaderInfo>

                    {authenticatedUser && authoredUser && !channelClosed && (
                        <StyledHeaderMenu>
                            <IconButton
                                aria-label="More"
                                aria-controls="header-menu"
                                aria-haspopup="true"
                                onClick={handleClick}
                                size="small"
                            >
                                <MoreIcon />
                            </IconButton>

                            <Menu
                                id="header-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                            >
                                <MenuItem
                                    onClick={(e: any) => handlePrintDialog()}
                                >
                                    {t("dialogs.poll.export_to_qr")}
                                </MenuItem>

                                <MenuItem
                                    disabled={hasVotes}
                                    onClick={(e: any) => handleEditPoll(e)}
                                    title={
                                        hasVotes
                                            ? (t(
                                                  "dialogs.poll.edit_poll_locked"
                                              ) as string)
                                            : undefined
                                    }
                                >
                                    {t("dialogs.poll.edit_poll")}
                                </MenuItem>

                                {isActive ? (
                                    <MenuItem
                                        onClick={(e: any) =>
                                            setOpenClosePollDialog(true)
                                        }
                                    >
                                        {t("dialogs.poll.close_poll")}
                                    </MenuItem>
                                ) : null}

                                <MenuItem
                                    onClick={(e: any) => handleODeletePoll(e)}
                                >
                                    {t("dialogs.poll.delete")}
                                </MenuItem>
                            </Menu>
                        </StyledHeaderMenu>
                    )}
                </StyledHeaderInnerWrapper>
            </StyledHeaderWrapper>

            <ClosePoll
                pollId={pollId}
                channelId={channelId}
                openDialog={openClosePollDialog}
                onClose={closeDialog}
            />
        </>
    );
};

export default PollItemHeader;
